import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CircleStopIcon, ShieldCheckIcon } from 'lucide-react';
import { AnnaAvatar } from '../avatar/AnnaAvatar';
import { noodstopKeeps, noodstopStops } from '../../data/rules';
import { useMotionPreference } from '../../hooks/useMotionPreference';
interface NoodstopSheetProps {
  open: boolean;
  onResume: () => void;
  size?: 'sm' | 'wall';
}

/** "Stopt / blijft werken" — the same sheet on every screen. */
export function NoodstopSheet({
  open,
  onResume,
  size = 'sm'
}: NoodstopSheetProps) {
  const reduce = useMotionPreference();
  const resumeRef = useRef<HTMLButtonElement>(null);
  const wall = size === 'wall';
  const body = wall ? 'text-[22px]' : 'text-[15px]';
  useEffect(() => {
    if (open) resumeRef.current?.focus();
  }, [open]);
  return <AnimatePresence data-id="element-1166" data-mp="3jifdq2szhtvz">
      {open && <motion.div className="fixed inset-0 z-[70] grid place-items-center overflow-y-auto bg-night-950/85 p-4 backdrop-blur-md" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-1167" data-mp="3fh28nuxptuq4">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="noodstop-title" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className={`w-full rounded-3xl border border-white/10 bg-night-900 ${wall ? 'max-w-[1040px] p-10' : 'max-w-[720px] p-6 sm:p-8'}`} data-id="element-1168" data-mp="n15ukzldwplq">
            <div className="flex items-center gap-5" data-id="element-1169" data-mp="1aodzvhitbynr">
              <div className={`shrink-0 ${wall ? 'h-20 w-20' : 'h-16 w-16'}`} data-id="element-1170" data-mp="37jjslw1sjytf">
                <AnnaAvatar state="gestopt" data-id="element-1171" data-mp="3chlhixlwesct" />
              </div>
              <div data-id="element-1172" data-mp="3cjxuewcsucj0">
                <h2 id="noodstop-title" className={`font-semibold text-ink ${wall ? 'text-[34px]' : 'text-[24px]'}`} data-id="element-1173" data-mp="34h5owg29wfoq">
                  Anna is gestopt
                </h2>
                <p className={`text-ink-soft ${wall ? 'text-[22px]' : 'text-[16px]'}`} data-id="element-1174" data-mp="30hn2tgyptykb">
                  Er gebeurt niets automatisch tot je haar hervat.
                </p>
              </div>
            </div>
            <div className={`grid gap-8 sm:grid-cols-2 ${wall ? 'mt-10' : 'mt-8'}`} data-id="element-1175" data-mp="39il965wmmi62">
              <section data-id="element-1176" data-mp="3ndx2c8v4w0kv">
                <h3 className={`flex items-center gap-2 font-semibold text-ink ${wall ? 'text-[24px]' : 'text-[15px]'}`} data-id="element-1177" data-mp="3jeeh1bzehnhi">
                  <CircleStopIcon className={wall ? 'h-6 w-6 text-alarm' : 'h-4 w-4 text-alarm'} aria-hidden data-id="element-1178" data-mp="10orqbzltt5i" /> Stopt
                </h3>
                <ul className="mt-3 space-y-2.5" data-id="element-1179" data-mp="3s79n27wozn8c">
                  {noodstopStops.map(item => <li key={item} className={`leading-snug text-ink-soft ${body}`} data-id="element-1180" data-mp="ayusurdl6v9b">{item}</li>)}
                </ul>
              </section>
              <section data-id="element-1181" data-mp="pl1x9zwwx09d">
                <h3 className={`flex items-center gap-2 font-semibold text-ink ${wall ? 'text-[24px]' : 'text-[15px]'}`} data-id="element-1182" data-mp="pmmrfofbeh6p">
                  <ShieldCheckIcon className={wall ? 'h-6 w-6 text-accent' : 'h-4 w-4 text-accent'} aria-hidden data-id="element-1183" data-mp="tksvgoorqinx" /> Blijft werken
                </h3>
                <ul className="mt-3 space-y-2.5" data-id="element-1184" data-mp="klp838ikjh3m">
                  {noodstopKeeps.map(item => <li key={item} className={`leading-snug text-ink-soft ${body}`} data-id="element-1185" data-mp="5x2kkjisfj1b">{item}</li>)}
                </ul>
              </section>
            </div>
            <div className={`flex justify-end ${wall ? 'mt-10' : 'mt-8'}`} data-id="element-1186" data-mp="3t20i4c86gsow">
              <button ref={resumeRef} type="button" onClick={onResume} className={`whitespace-nowrap rounded-full bg-ink font-semibold text-night-950 transition-colors duration-150 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${wall ? 'h-16 px-10 text-[22px]' : 'h-12 px-6 text-[15px]'}`} data-id="element-1187" data-mp="3szxnfew2ybdu">
                Hervat Anna
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}